// TypeScript port of `Filters` + `_where_and_params` from
// src/pwhlstats/web/queries.py.
//
// The output of `whereAndParams(filters)` must be byte-identical to the
// Python version for the same filter dict. Parity is verified by
// tests/test_filter_port.py over a golden corpus.
//
// Keep this file mechanical — name a constant the same as its Python
// counterpart, emit the SQL in the same order, use the same SQL
// formatting whitespace. Drift here is invisible until a query returns
// the wrong rows, so the parity test must catch it.

import {
  chainStartedOffFaceoff,
  chainStartedOffRush,
  isRebound,
  lateGame,
} from "./predicates";

// -- Constants (mirror queries.py top of file) -------------------------------

export const SCORE_STATES: Record<string, string> = {
  leading: "e.score_diff_for > 0",
  trailing: "e.score_diff_for < 0",
  tied: "e.score_diff_for = 0",
};

export const STRENGTH_STATES: Record<string, string> = {
  "5v5": "5v5 (even strength)",
  pp: "PP (5v4, 5v3, 4v3)",
  pk: "PK (4v5, 3v5, 3v4)",
  "4v4": "4v4",
  "3v3": "3v3",
  en: "Empty net",
};

const HOME_ADVANTAGE = ["5v4", "5v3", "4v3"] as const;
const AWAY_ADVANTAGE = ["4v5", "3v5", "3v4"] as const;
const HOME_EN = ["6v5", "6v4", "6v3"] as const;
const AWAY_EN = ["5v6", "4v6", "3v6"] as const;
const HOME_PK_STATES = [...AWAY_ADVANTAGE, ...AWAY_EN] as const;
const AWAY_PK_STATES = [...HOME_ADVANTAGE, ...HOME_EN] as const;

function tupleSql(values: readonly string[]): string {
  return "(" + values.map((v) => `'${v}'`).join(",") + ")";
}

export function strengthPredForEvents(key: string): string | null {
  if (key === "5v5" || key === "4v4" || key === "3v3") {
    return `e.strength_state = '${key}'`;
  }
  if (key === "pp") {
    return (
      `((e.team_id = g.home_team_id AND e.strength_state IN ${tupleSql(HOME_ADVANTAGE)}) ` +
      `OR (e.team_id = g.away_team_id AND e.strength_state IN ${tupleSql(AWAY_ADVANTAGE)}))`
    );
  }
  if (key === "pk") {
    return (
      `((e.team_id = g.home_team_id AND e.strength_state IN ${tupleSql(AWAY_ADVANTAGE)}) ` +
      `OR (e.team_id = g.away_team_id AND e.strength_state IN ${tupleSql(HOME_ADVANTAGE)}))`
    );
  }
  if (key === "en") {
    return (
      `((e.team_id = g.home_team_id AND e.strength_state IN ${tupleSql(HOME_EN)}) ` +
      `OR (e.team_id = g.away_team_id AND e.strength_state IN ${tupleSql(AWAY_EN)}))`
    );
  }
  return null;
}

export function strengthPredForSkater(key: string): string | null {
  if (key === "5v5" || key === "4v4" || key === "3v3") {
    return `e.strength_state = '${key}'`;
  }
  if (key === "pp") {
    return (
      `((s.side='home'    AND e.strength_state IN ${tupleSql(HOME_ADVANTAGE)}) ` +
      `OR (s.side='visitor' AND e.strength_state IN ${tupleSql(AWAY_ADVANTAGE)}))`
    );
  }
  if (key === "pk") {
    return (
      `((s.side='home'    AND e.strength_state IN ${tupleSql(AWAY_ADVANTAGE)}) ` +
      `OR (s.side='visitor' AND e.strength_state IN ${tupleSql(HOME_ADVANTAGE)}))`
    );
  }
  if (key === "en") {
    return (
      `((s.side='home'    AND e.strength_state IN ${tupleSql(HOME_EN)}) ` +
      `OR (s.side='visitor' AND e.strength_state IN ${tupleSql(AWAY_EN)}))`
    );
  }
  return null;
}

export function strengthPredForGoalie(key: string): string | null {
  if (key === "5v5" || key === "4v4" || key === "3v3") {
    return `e.strength_state = '${key}'`;
  }
  if (key === "pk") {
    return (
      `((e.team_id = g.away_team_id AND e.strength_state IN ${tupleSql(HOME_PK_STATES)}) ` +
      `OR (e.team_id = g.home_team_id AND e.strength_state IN ${tupleSql(AWAY_PK_STATES)}))`
    );
  }
  if (key === "pp") {
    return (
      `((e.team_id = g.away_team_id AND e.strength_state IN ${tupleSql(HOME_ADVANTAGE)}) ` +
      `OR (e.team_id = g.home_team_id AND e.strength_state IN ${tupleSql(AWAY_ADVANTAGE)}))`
    );
  }
  return null;
}

export const EVENT_TYPE_GROUPS: Record<string, [string, string]> = {
  shots_on_net: ["e.event_type IN ('shot','goal')", "Shots on net"],
  goals: ["e.event_type = 'goal'", "Goals"],
  shot_attempts: ["e.event_type IN ('shot','goal','blocked_shot')", "All shot attempts"],
  faceoffs: ["e.event_type = 'faceoff'", "Faceoffs"],
  hits: ["e.event_type = 'hit'", "Hits"],
  penalties: ["e.event_type = 'penalty'", "Penalties"],
};

export const ZONES = ["slot", "perimeter", "point", "behind_net", "neutral"];

export const GOALIE_STATES: Record<string, string> = {
  normal: "Both goalies on",
  en_for: "Empty net for (extra attacker)",
  en_against: "Empty net against",
  both_pulled: "Both goalies pulled",
};

export const SCORE_BUCKETS: Record<string, string> = {
  leading_3plus: "Leading by 3+",
  leading_1_2: "Leading by 1–2",
  tied: "Tied",
  trailing_1_2: "Trailing by 1–2",
  trailing_3plus: "Trailing by 3+",
};

export const PERIOD_STATES: Record<string, string> = {
  regulation: "Regulation",
  ot_3v3: "OT (3v3)",
  shootout: "Shootout",
};

export const PREV_EVENT_TYPES = [
  "shot",
  "blocked_shot",
  "goal",
  "faceoff",
  "hit",
  "penalty",
] as const;

export const PREV_EVENT_TEAM_OPTIONS = ["same", "opponent", "either"] as const;

export const SEASON_TYPES = ["regular", "playoff", "preseason"] as const;

// -- Filters ------------------------------------------------------------------

// SQLite param values arrive as int (game_id, player_id, period), str
// (zone, season_type), or null. JS represents int as number. The Python
// list emits these heterogeneously; preserve that via FilterParam.
export type FilterParam = number | string;

export interface Filters {
  team_id: number | null;
  opponent_id: number | null;
  player_id: number | null;
  goalie_id: number | null;
  on_ice_player_id: number | null;
  on_ice_with_player_id: number | null;
  on_ice_without_player_id: number | null;
  event_group: string;
  zone: string | null;
  score_state: string | null;
  strength_state: string | null;
  period: number | null;
  clock_seconds_min: number | null;
  clock_seconds_max: number | null;
  prev_event_types: string[] | null;
  prev_event_team: string | null;
  prev_event_zones: string[] | null;
  time_since_prev_event_max_seconds: number | null;
  no_intervening_reset: boolean;
  is_rebound_only: boolean;
  pressure_sequence_only: boolean;
  game_id: number | null;
  game_ids: number[] | null;
  goalie_state: string | null;
  score_bucket: string | null;
  period_state: string | null;
  late_game: boolean | null;
  delayed_penalty: boolean | null;
  chain_started_off_faceoff: boolean | null;
  chain_started_off_rush: boolean | null;
  chain_min_events: number | null;
  chain_id: number | null;
  season_type: string | null;
  limit: number;
  offset: number;
}

export const FILTER_DEFAULTS: Filters = {
  team_id: null,
  opponent_id: null,
  player_id: null,
  goalie_id: null,
  on_ice_player_id: null,
  on_ice_with_player_id: null,
  on_ice_without_player_id: null,
  event_group: "shots_on_net",
  zone: null,
  score_state: null,
  strength_state: null,
  period: null,
  clock_seconds_min: null,
  clock_seconds_max: null,
  prev_event_types: null,
  prev_event_team: null,
  prev_event_zones: null,
  time_since_prev_event_max_seconds: null,
  no_intervening_reset: false,
  is_rebound_only: false,
  pressure_sequence_only: false,
  game_id: null,
  game_ids: null,
  goalie_state: null,
  score_bucket: null,
  period_state: null,
  late_game: null,
  delayed_penalty: null,
  chain_started_off_faceoff: null,
  chain_started_off_rush: null,
  chain_min_events: null,
  chain_id: null,
  season_type: null,
  limit: 100,
  offset: 0,
};

export function applyFilterDefaults(partial: Partial<Filters>): Filters {
  return { ...FILTER_DEFAULTS, ...partial };
}

// -- Internal helpers (mirror queries.py) ------------------------------------

function onIcePred(_playerId: number, negate: boolean = false): string {
  const op = negate ? "NOT EXISTS" : "EXISTS";
  return (
    `${op} (SELECT 1 FROM shifts s ` +
    `WHERE s.game_id = e.game_id AND s.player_id = ? ` +
    `AND s.start_seconds <= e.clock_seconds_elapsed ` +
    `AND e.clock_seconds_elapsed < s.end_seconds)`
  );
}

function prevEventSubquery(extraWhere: string = ""): string {
  let where = "p.game_id = e.game_id AND p.clock_seconds_elapsed < e.clock_seconds_elapsed";
  if (extraWhere) where += ` AND ${extraWhere}`;
  return (
    `SELECT p.clock_seconds_elapsed AS p_t, p.event_type AS p_et, ` +
    `p.team_id AS p_team, p.zone AS p_zone ` +
    `FROM events p WHERE ${where} ` +
    `ORDER BY p.clock_seconds_elapsed DESC, p.event_id DESC LIMIT 1`
  );
}

// -- whereAndParams (the main port) ------------------------------------------

export interface WhereAndParamsResult {
  where: string;
  params: FilterParam[];
}

export function whereAndParams(f: Filters): WhereAndParamsResult {
  const eventGroup =
    EVENT_TYPE_GROUPS[f.event_group] ?? EVENT_TYPE_GROUPS["shots_on_net"];
  const eventPred = eventGroup[0];
  const wheres: string[] = [eventPred];
  const params: FilterParam[] = [];

  if (f.team_id !== null) {
    wheres.push("e.team_id = ?");
    params.push(f.team_id);
  }
  if (f.opponent_id !== null) {
    wheres.push(
      "((g.home_team_id = ? AND g.away_team_id = ?) OR (g.home_team_id = ? AND g.away_team_id = ?))",
    );
    if (f.team_id !== null) {
      params.push(f.team_id, f.opponent_id, f.opponent_id, f.team_id);
    } else {
      wheres[wheres.length - 1] = "(g.home_team_id = ? OR g.away_team_id = ?)";
      params.push(f.opponent_id, f.opponent_id);
    }
  }
  if (f.player_id !== null) {
    wheres.push("e.player_id = ?");
    params.push(f.player_id);
  }
  if (f.goalie_id !== null) {
    wheres.push("e.goalie_id = ?");
    params.push(f.goalie_id);
  }
  if (f.on_ice_player_id !== null) {
    wheres.push(onIcePred(f.on_ice_player_id));
    params.push(f.on_ice_player_id);
  }
  if (f.on_ice_with_player_id !== null) {
    wheres.push(onIcePred(f.on_ice_with_player_id));
    params.push(f.on_ice_with_player_id);
  }
  if (f.on_ice_without_player_id !== null) {
    wheres.push(onIcePred(f.on_ice_without_player_id, true));
    params.push(f.on_ice_without_player_id);
  }
  if (f.zone) {
    wheres.push("e.zone = ?");
    params.push(f.zone);
  }
  if (f.score_state && f.score_state in SCORE_STATES) {
    wheres.push(SCORE_STATES[f.score_state]!);
  }
  if (f.strength_state && f.strength_state in STRENGTH_STATES) {
    const pred = strengthPredForEvents(f.strength_state);
    if (pred) wheres.push(pred);
  }
  if (f.period !== null) {
    wheres.push("e.period = ?");
    params.push(f.period);
  }
  if (f.clock_seconds_min !== null) {
    wheres.push("e.clock_seconds_elapsed >= ?");
    params.push(f.clock_seconds_min);
  }
  if (f.clock_seconds_max !== null) {
    wheres.push("e.clock_seconds_elapsed <= ?");
    params.push(f.clock_seconds_max);
  }

  // ----- Prev-event primitives (composable) -----
  const prevClauses: string[] = [];
  const validPrevTypes = (f.prev_event_types ?? []).filter((t) =>
    (PREV_EVENT_TYPES as readonly string[]).includes(t),
  );
  const validPrevZones = (f.prev_event_zones ?? []).filter((z) =>
    ZONES.includes(z),
  );
  const prevTeamUsable =
    f.prev_event_team !== null &&
    f.prev_event_team !== "either" &&
    (PREV_EVENT_TEAM_OPTIONS as readonly string[]).includes(f.prev_event_team);
  const needsPrev =
    validPrevTypes.length > 0 ||
    prevTeamUsable ||
    validPrevZones.length > 0 ||
    f.time_since_prev_event_max_seconds !== null;

  if (needsPrev) {
    const sub = prevEventSubquery();
    const prevAlias = `(${sub}) prev`;
    const outer: string[] = [];
    if (validPrevTypes.length > 0) {
      outer.push("prev.p_et IN (" + Array(validPrevTypes.length).fill("?").join(",") + ")");
      params.push(...validPrevTypes);
    }
    if (f.prev_event_team === "same") {
      outer.push("prev.p_team = e.team_id");
    } else if (f.prev_event_team === "opponent") {
      outer.push("prev.p_team IS NOT NULL AND prev.p_team != e.team_id");
    }
    if (validPrevZones.length > 0) {
      outer.push("prev.p_zone IN (" + Array(validPrevZones.length).fill("?").join(",") + ")");
      params.push(...validPrevZones);
    }
    if (f.time_since_prev_event_max_seconds !== null) {
      outer.push("(e.clock_seconds_elapsed - prev.p_t) <= ?");
      // Python casts with int(); JS numbers are already integral via the
      // interface contract. Push as-is.
      params.push(f.time_since_prev_event_max_seconds);
    }
    const outerSql = outer.length > 0 ? " AND " + outer.join(" AND ") : "";
    prevClauses.push(`EXISTS (SELECT 1 FROM ${prevAlias} WHERE 1=1${outerSql})`);
  }

  if (f.no_intervening_reset) {
    const sub = prevEventSubquery();
    prevClauses.push(
      `NOT EXISTS (SELECT 1 FROM events m, (${sub}) prev ` +
        "WHERE m.game_id = e.game_id " +
        "AND m.clock_seconds_elapsed > prev.p_t " +
        "AND m.clock_seconds_elapsed < e.clock_seconds_elapsed " +
        "AND (m.event_type = 'faceoff' OR " +
        "     (m.event_type IN ('shot','goal','blocked_shot') " +
        "      AND m.team_id IS NOT NULL AND m.team_id != e.team_id)))",
    );
  }

  if (f.is_rebound_only) {
    prevClauses.push(isRebound());
  }

  if (f.pressure_sequence_only) {
    prevClauses.push("e.pressure_sequence_tag = 1");
  }

  wheres.push(...prevClauses);

  if (f.game_id !== null) {
    wheres.push("e.game_id = ?");
    params.push(f.game_id);
  }
  if (f.game_ids && f.game_ids.length > 0) {
    const placeholders = Array(f.game_ids.length).fill("?").join(",");
    wheres.push(`e.game_id IN (${placeholders})`);
    params.push(...f.game_ids);
  }
  if (
    f.season_type &&
    (SEASON_TYPES as readonly string[]).includes(f.season_type)
  ) {
    if (f.season_type === "regular") {
      wheres.push("(g.season_type = ? OR g.season_type IS NULL)");
    } else {
      wheres.push("g.season_type = ?");
    }
    params.push(f.season_type);
  }
  if (f.goalie_state && f.goalie_state in GOALIE_STATES) {
    wheres.push("e.goalie_state = ?");
    params.push(f.goalie_state);
  }
  if (f.score_bucket && f.score_bucket in SCORE_BUCKETS) {
    wheres.push("e.score_bucket = ?");
    params.push(f.score_bucket);
  }
  if (f.period_state && f.period_state in PERIOD_STATES) {
    wheres.push("e.period_state = ?");
    params.push(f.period_state);
  }
  if (f.late_game) {
    wheres.push(lateGame());
  }
  if (f.delayed_penalty) {
    wheres.push("e.delayed_penalty = 1");
  }
  if (f.chain_started_off_faceoff !== null) {
    wheres.push(chainStartedOffFaceoff(f.chain_started_off_faceoff));
  }
  if (f.chain_started_off_rush !== null) {
    wheres.push(chainStartedOffRush(f.chain_started_off_rush));
  }
  if (f.chain_min_events !== null) {
    wheres.push("pc.num_events >= ?");
    params.push(f.chain_min_events);
  }
  if (f.chain_id !== null) {
    wheres.push("e.possession_chain_id = ?");
    params.push(f.chain_id);
  }
  return { where: wheres.join(" AND "), params };
}
